
import { ref, computed, defineComponent, getCurrentInstance, watch } from "vue-demi";
import axios from "@/plugins/http";
import { API_ROUTES } from "@/utils/API";
import {
  API_TNotificationsShortList,
  API_TNotificationsShortListItem,
} from "@/models/notifications";
import { SMUser } from "@/store";

export default defineComponent({
  name: "Popover",

  components: {
    NotificationsPopoverTaskTemplate: () =>
      import("@/modules/Notifications/NotificationsPopover/NotificationsPopoverTaskTemplate.vue"),
    NotificationsEmpty: () => import("@/modules/Notifications/components/NotificationsEmpty.vue"),
    TabsSlider: () => import("@/modules/Pricing/Subscription/TabsSlider.vue"),
  },

  props: {
    to: {
      type: String,
      default: "root",
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
    position: {
      type: Object as () => { top: number; right: number },
      default: () => ({ top: 0, right: 0 }),
    },
  },

  emits: ["update:modelValue", "cancel", "ok"],

  setup(props, { emit }) {
    const isOpen = computed({
      get: () => props.modelValue,
      set: (value) => emit("update:modelValue", value),
    });

    const onCancel = () => {
      emit("cancel");
    };

    const onOk = () => {
      emit("ok");
    };

    const selectedTab = ref(0);

    const newsCount = ref(0);
    const tasksCount = ref(0);
    const news = ref<API_TNotificationsShortListItem[]>([]);
    const tasks = ref<API_TNotificationsShortListItem[]>([]);

    const isComplianceBlocked = computed(() => SMUser.isComplianceBlocked);

    const isNotSponsor = computed(() => SMUser.isNotSponsor);

    const isTasksHidden = computed(() => isComplianceBlocked.value || isNotSponsor.value);

    const items = computed(() => {
      return selectedTab.value ? news.value : tasks.value;
    });

    async function fetchNotificationsShortList() {
      try {
        const { data } = await axios.get<API_TNotificationsShortList>(
          API_ROUTES.NOTIFICATIONS_SHORT_LIST
        );

        if (!data) return;

        newsCount.value = data.news_count;
        tasksCount.value = data.tasks_count;
        news.value = data.news;
        tasks.value = data.tasks;

        if (isTasksHidden.value) {
          tasksCount.value = 0;
          tasks.value = [];
        }

        if (!tasksCount.value && newsCount.value) selectedTab.value = 1;
      } catch (error) {
        console.error(`Error fetching NOTIFICATIONS_SHORT_LIST:`, error);
      }
    }

    const tabs = computed(() => [
      { title: "Tasks", count: tasksCount.value },
      { title: "Updates", count: newsCount.value },
    ]);

    watch(
      isOpen,
      async (newId, oldId) => {
        if (newId === oldId) return;

        if (newId) {
          fetchNotificationsShortList();
        }
      },
      { immediate: true }
    );

    const router = getCurrentInstance()?.proxy?.$router;

    async function openItem(id: number) {
      try {
        if (selectedTab.value === 0) {
          await router.push(`${SMUser.basePath}/tasks/${id}`);
        } else {
          await router.push(`${SMUser.basePath}/notifications/${id}`);
        }

        onCancel();
      } catch {
        return;
      }
    }

    async function openNotifications() {
      onCancel();

      try {
        await router.push(`${SMUser.basePath}/notifications`);
      } catch {
        return;
      }
    }

    return {
      isOpen,
      onCancel,
      onOk,
      close,
      selectedTab,
      newsCount,
      tasksCount,
      items,
      tabs,
      openItem,
      openNotifications,
    };
  },
});
